<template>
	<div class="select-list-container" role="radiogroup">
		<div
			v-for="option in options"
			:key="option.value"
			:class="{ disabled: disabled || !option.accountEnabled }"
			class="option-container"
			role="radio"
			:aria-label="
				$t('paymentType', { type: option.name, balance: option.amount })
			"
			:aria-disabled="disabled || !option.accountEnabled"
			:aria-checked="isOptionSelected(option)"
			:tabindex="disabled ? -1 : 0"
			@click="selectOption(option)"
			@keydown.enter="selectOption(option)"
			@mousedown.prevent
		>
			<div class="multiselect-container">
				<ja-multiselect-circle
					aria-hidden="true"
					tabindex="-1"
					:selected="isOptionSelected(option)"
					:disabled="disabled"
				></ja-multiselect-circle>
				<div class="option-text body-text-1" aria-hidden="true">
					{{ option.name }}
					<div v-if="option.interest" class="body-text-1" aria-hidden="true">
						&#40;{{ $t('availableCredit') }}: {{ option.amount | currency }} |{{
							' ' + $t('interest')
						}}: {{ convertInterestToPercentage(option.interest) + '%' }}&#41;
					</div>
					<span v-if="option.balanceNotEnough"
						>{{ $t('notEnoughBalance') }}
					</span>
				</div>
			</div>

			<div
				v-if="option.amount && !option.interest"
				class="body-text-1"
				aria-hidden="true"
			>
				{{ option.amount | currency }}
			</div>
		</div>
	</div>
</template>
<script>
import MultiselectCircle from '@/components/ui/MultiselectCircle';
import LifeStageData from '@/mixins/LifeStageData';
import { defaultNames } from '@/helpers/constants';

export default {
	components: {
		jaMultiselectCircle: MultiselectCircle
	},
	mixins: [LifeStageData],

	props: {
		options: {
			type: Array,
			default: () => {
				return [];
			}
		},
		selectedOption: {
			type: [Array, Object],
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		defaultSelectedOption: {
			type: Boolean,
			default: false
		},
		optionDefault: {
			type: Object,
			default: null
		},
		disableDeselection: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			selectedOptions: [],
			option: null,
			isAnyOptionDisabled: false
		};
	},
	watch: {
		options(newValue) {
			this.isAnyOptionDisabled = newValue.some(
				option => !option.accountEnabled
			);
			if (this.isAnyOptionDisabled) {
				this.selectedOptions = this.selectedOptions.filter(
					option => option.accountEnabled
				);
			}
			if (this.defaultSelectedOption) {
				this.selectOption(this.options[0]);
			}
		},
		selectedOption(newValue) {
			if (!newValue || !newValue.length) {
				this.selectedOptions = [];
			}
		}
	},
	mounted() {
		if (this.defaultSelectedOption && this.options) {
			this.selectOption(this.options[0]);
		} else if (this.optionDefault && this.options) {
			const indexPreSelected = this.options.findIndex(
				option => option.value === this.optionDefault.value
			);
			if (indexPreSelected !== -1) {
				this.selectOption(this.options[indexPreSelected]);
			} else {
				this.selectOption(this.options[0]);
			}
		}
	},
	methods: {
		convertInterestToPercentage(interest) {
			return interest < 1 ? interest * 100 : interest;
		},
		isOptionSelected(option) {
			if (this.disabled) return false;
			return (
				this.selectedOptions.some(
					selectedOption =>
						selectedOption.name.toLowerCase() === option.name.toLowerCase()
				) && option.accountEnabled
			);
		},
		selectOption(option) {
			if (option && option.accountEnabled) {
				if (this.disabled || !option.accountEnabled) return;

				this.option = option;

				const isAutoLoan =
					option.defaultName === defaultNames.AUTO_LOAN ||
					option.defaultName === defaultNames.AUTO_LOAN_DEALERSHIP;

				const isOptionAlreadySelected = this.selectedOptions.some(
					selectedOption =>
						selectedOption.defaultName.toLowerCase() ===
						option.defaultName.toLowerCase()
				);

				const autoLoanOption = this.selectedOptions.find(
					selectedOption =>
						selectedOption.defaultName === defaultNames.AUTO_LOAN
				);
				const autoLoanDealershipOption = this.selectedOptions.find(
					selectedOption =>
						selectedOption.defaultName === defaultNames.AUTO_LOAN_DEALERSHIP
				);

				if (isAutoLoan && !isOptionAlreadySelected) {
					this.selectedOptions = [option];
				} else if (!isAutoLoan && !isOptionAlreadySelected) {
					if (autoLoanOption) {
						const currentOptionsCount = this.selectedOptions.length;
						if (currentOptionsCount < 2) {
							this.selectedOptions.push(option);
						} else {
							this.selectedOptions = [autoLoanOption, option];
						}
					} else if (autoLoanDealershipOption) {
						const currentOptionsCount = this.selectedOptions.length;
						if (currentOptionsCount < 2) {
							this.selectedOptions.push(option);
						} else {
							this.selectedOptions = [autoLoanDealershipOption, option];
						}
					} else {
						this.selectedOptions = [option];
					}
				} else if (isOptionAlreadySelected) {
					if (isAutoLoan && this.selectedOptions.length > 1) {
						this.selectedOptions = [option];
					} else {
						this.selectedOptions = this.selectedOptions.filter(
							selectedOption =>
								selectedOption.defaultName.toLowerCase() !==
								option.defaultName.toLowerCase()
						);
					}
				}
				if (!this.selectedOptions.length) {
					this.$emit('optionDeselected', this.option);
					if (this.disableDeselection) {
						this.selectedOptions = [this.option];
					}
				}
			}
			this.$emit('optionSelected', this.selectedOptions);
		}
	}
};
</script>
<style lang="scss" scoped>
.select-list-container {
	display: flex;
	flex-direction: column;
	width: 100%;

	.option-container {
		display: flex;
		margin-bottom: 16px;
		justify-content: space-between;

		&.disabled {
			pointer-events: none;
		}
		&.disabled * {
			color: $icon-disabled;
			fill: $icon-disabled;
		}

		.multiselect-container {
			display: flex;

			&.disabled * {
				color: $icon-disabled;
				fill: $icon-disabled;
			}
		}

		:hover {
			cursor: pointer;
		}

		&.disabled {
			:hover {
				cursor: default;
			}
		}

		.option-text {
			margin-right: 5px;
		}
	}
}
</style>
